import { useMemo, useState, type ReactNode } from "react";

/**
 * Los componentes de esta app, EXTRAÍDOS de sus pantallas y no inventados.
 *
 * El análisis de la v1 lo midió: catorce pestañas, catorce tablas, y los
 * encabezados ordenables existían en UNA porque alguien los reclamó. La
 * consistencia vivía en copiar bien. Aquí lo que se repite tres veces es un
 * componente, y el componente trae el COMPORTAMIENTO, no solo el estilo: la
 * tabla ya ordena, la cifra ya exige a dónde llevar, el vacío ya dice qué
 * significa y qué hacer.
 *
 * DOS REGLAS DE LA CASA HORNEADAS AQUÍ, para que no haya que recordarlas:
 *
 *   · «Todo número visible se puede tocar y lleva al trabajo que
 *     representa.» `Cifra` exige `alAbrir`; si de verdad no hay a dónde ir,
 *     se dice por qué con `sinAccion`, y el tipo no deja omitir las dos.
 *   · Ningún estado se comunica solo con color (M-37): la insignia lleva
 *     texto, la barra de progreso lleva su número, la alerta lleva su nivel
 *     escrito.
 */

// ---------------------------------------------------------------- pantalla

/** La anatomía fija de toda pantalla: título, acciones, contenido. Una
 *  pantalla nueva son sus datos y sus verbos, no su layout. */
export function Pantalla({
  titulo,
  descripcion,
  acciones,
  children,
}: {
  titulo: string;
  descripcion?: ReactNode;
  acciones?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="panel">
      <div className="encabezado">
        <div>
          <h1>{titulo}</h1>
          {descripcion && <p className="ayuda">{descripcion}</p>}
        </div>
        {acciones && <div className="acciones">{acciones}</div>}
      </div>
      {children}
    </section>
  );
}

// ------------------------------------------------------------------ estados

export function Cargando({ que }: { que: string }) {
  return (
    <p className="apagado" role="status">
      Cargando {que}…
    </p>
  );
}

export function ErrorDeCarga({
  mensaje,
  reintentar,
}: {
  mensaje: string;
  reintentar?: () => void;
}) {
  return (
    <div className="error" role="alert">
      <p>{mensaje}</p>
      {reintentar && (
        <button type="button" className="btn secundario" onClick={reintentar}>
          Volver a intentar
        </button>
      )}
    </div>
  );
}

/** El vacío dice qué significa y qué hacer, no solo «no hay datos». */
export function Vacio({
  titulo,
  detalle,
  accion,
}: {
  titulo: string;
  detalle?: string;
  accion?: ReactNode;
}) {
  return (
    <div className="vacio">
      <p className="vacio-titulo">{titulo}</p>
      {detalle && <p className="ayuda">{detalle}</p>}
      {accion}
    </div>
  );
}

// ---------------------------------------------------------------- insignia

export type Tono = "exito" | "aviso" | "peligro" | "neutro" | "primario";

export function Insignia({ tono = "neutro", children }: { tono?: Tono; children: ReactNode }) {
  return <span className={`etiqueta tono-${tono}`}>{children}</span>;
}

// ------------------------------------------------------------------- cifra

type AccionDeCifra =
  | { alAbrir: () => void; sinAccion?: never; enlace?: never }
  | { enlace: string; alAbrir?: never; sinAccion?: never }
  | { sinAccion: string; alAbrir?: never; enlace?: never };

/**
 * Una cifra grande con su etiqueta. ES UN BOTÓN: lleva al trabajo que
 * representa. Cuando de verdad no hay a dónde ir, `sinAccion` dice por qué
 * en un `title` y se dibuja como texto — y queda escrito en el código que
 * alguien lo decidió.
 */
export function Cifra({
  etiqueta,
  valor,
  detalle,
  tono = "neutro",
  ...accion
}: {
  etiqueta: string;
  valor: ReactNode;
  detalle?: ReactNode;
  tono?: Tono;
} & AccionDeCifra) {
  const dentro = (
    <>
      <span className="cifra-valor">{valor}</span>
      <span className="cifra-etiqueta">{etiqueta}</span>
      {detalle && <span className="cifra-detalle">{detalle}</span>}
    </>
  );
  const clase = `cifra tono-${tono}`;
  if ("alAbrir" in accion) {
    return (
      <button type="button" className={clase} onClick={accion.alAbrir}>
        {dentro}
      </button>
    );
  }
  if ("enlace" in accion) {
    return (
      <a className={clase} href={accion.enlace}>
        {dentro}
      </a>
    );
  }
  return (
    <div className={`${clase} sin-accion`} title={accion.sinAccion}>
      {dentro}
    </div>
  );
}

// ---------------------------------------------------------------- progreso

/** Barra con su número: el color es apoyo, el texto es el dato. */
export function Progreso({
  valor,
  max,
  texto,
  tono = "primario",
}: {
  valor: number;
  max: number;
  texto: string;
  tono?: Tono;
}) {
  const pct = max > 0 ? Math.min(100, Math.round((100 * valor) / max)) : 0;
  return (
    <div
      className={`progreso tono-${tono}`}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={Math.min(valor, max)}
      aria-valuetext={texto}
    >
      <div className="progreso-barra" style={{ width: `${pct}%` }} />
      <span className="progreso-texto">{texto}</span>
    </div>
  );
}

// ------------------------------------------------------------------- tabla

export interface Columna<T> {
  id: string;
  titulo: ReactNode;
  /** Lo que se dibuja en la celda. */
  celda: (fila: T) => ReactNode;
  /** Por qué se ordena; si falta, la columna no ordena. */
  clave?: (fila: T) => number | string | null;
  numero?: boolean;
  /** `true` esconde el título visualmente (acciones). */
  sinTitulo?: boolean;
}

/**
 * La tabla que ORDENA DE SERIE (D-50). Quien la usa declara columnas y
 * filas; el encabezado es un botón real dentro del `th`, con `aria-sort`, y
 * las columnas numéricas van a la derecha con cifras tabulares.
 */
export function Tabla<T>({
  columnas,
  filas,
  claveDe,
  caption,
  vacio,
  ordenInicial,
  claseDeFila,
  alTocarFila,
}: {
  columnas: Columna<T>[];
  filas: T[];
  claveDe: (fila: T) => string | number;
  caption: string;
  vacio: ReactNode;
  ordenInicial?: { columna: string; desc?: boolean };
  claseDeFila?: (fila: T) => string;
  alTocarFila?: (fila: T) => void;
}) {
  const [orden, setOrden] = useState<{ columna: string; desc: boolean } | null>(
    ordenInicial ? { columna: ordenInicial.columna, desc: Boolean(ordenInicial.desc) } : null,
  );

  const ordenadas = useMemo(() => {
    if (!orden) return filas;
    const col = columnas.find((c) => c.id === orden.columna);
    if (!col?.clave) return filas;
    const k = col.clave;
    return [...filas].sort((a, b) => {
      const x = k(a);
      const y = k(b);
      if (x == null && y == null) return 0;
      if (x == null) return 1;
      if (y == null) return -1;
      const r = typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y), "es");
      return orden.desc ? -r : r;
    });
  }, [filas, orden, columnas]);

  function alternar(id: string) {
    setOrden((o) => (o?.columna === id ? { columna: id, desc: !o.desc } : { columna: id, desc: false }));
  }

  return (
    <div className="contenedor-tabla">
      <table className="tabla">
        <caption>{caption}</caption>
        <thead>
          <tr>
            {columnas.map((c) => {
              const activa = orden?.columna === c.id;
              const sort = activa ? (orden?.desc ? "descending" : "ascending") : undefined;
              return (
                <th
                  key={c.id}
                  scope="col"
                  className={c.numero ? "numero" : undefined}
                  aria-sort={c.clave ? (sort ?? "none") : undefined}
                >
                  {c.clave ? (
                    <button type="button" className="ordenar" onClick={() => alternar(c.id)}>
                      {c.sinTitulo ? <span className="solo-lectores">{c.titulo}</span> : c.titulo}
                      <span className="flecha" aria-hidden="true">
                        {activa ? (orden?.desc ? "▼" : "▲") : "⇅"}
                      </span>
                    </button>
                  ) : c.sinTitulo ? (
                    <span className="solo-lectores">{c.titulo}</span>
                  ) : (
                    c.titulo
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {ordenadas.length === 0 && (
            <tr>
              <td className="vacia" colSpan={columnas.length}>
                {vacio}
              </td>
            </tr>
          )}
          {ordenadas.map((f) => (
            <tr
              key={claveDe(f)}
              className={[claseDeFila?.(f) ?? "", alTocarFila ? "tocable" : ""].join(" ").trim() || undefined}
              onClick={alTocarFila ? () => alTocarFila(f) : undefined}
            >
              {columnas.map((c) => (
                <td key={c.id} className={c.numero ? "numero" : undefined}>
                  {c.celda(f)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
